/**
 * Status → Tone Mapping
 * 
 * Maps domain statuses to visual tones and theme color paths.
 * Every mapping returns { label, tone, themeColor } where themeColor
 * is a theme.palette.* path.
 */

// ============================================================================
// PROJECT STATUS
// ============================================================================

const PROJECT_STATUS_MAP = {
  // R-22: draft = saved but setup incomplete (no boundary yet). Neutral tone
  // per the ticket's guard — not an error, not a warning; it is an intermediate
  // state that a follow-up step moves to Active.
  draft:     { label: 'Draft',     tone: 'neutral', themeColor: 'grey.500' },
  active:    { label: 'Active',    tone: 'success', themeColor: 'success.main' },
  // R-22 follow-up: values user-selectable via the ProjectPage Status Select
  // (see ProjectPage.jsx MenuItem enum). Missing them from PROJECT_STATUS_MAP
  // caused Chip fallthrough to `default` (grey) on live projects that carried
  // these values. Tones mirror the prior hand-rolled getStatusColor mapping.
  inactive:  { label: 'Inactive',  tone: 'neutral', themeColor: 'grey.500' },
  completed: { label: 'Completed', tone: 'info',    themeColor: 'info.main' },
  paused:    { label: 'Paused',    tone: 'warning', themeColor: 'warning.main' },
  closed:    { label: 'Closed',    tone: 'neutral', themeColor: 'grey.500' },
};

// ============================================================================
// COUNT FRESHNESS
// ============================================================================

const COUNT_FRESHNESS_MAP = {
  current: { label: 'Current', tone: 'success', themeColor: 'success.main' },
  stale: { label: 'Stale', tone: 'warning', themeColor: 'warning.main' },
  none: { label: 'No counts', tone: 'neutral', themeColor: 'grey.500' },
};

// ============================================================================
// ENTRY CHANNEL
// ============================================================================

const ENTRY_CHANNEL_MAP = {
  'trucker-app': { label: 'Trucker', tone: 'info', themeColor: 'info.main' },
  desktop: { label: 'Desktop', tone: 'neutral', themeColor: 'grey.500' },
};

// ============================================================================
// CAPABILITY (TRACKING MODE)
// ============================================================================

const CAPABILITY_MAP = {
  beacon: { label: 'Beacon', tone: 'info', themeColor: 'info.main' },
  manual: { label: 'Manual', tone: 'success', themeColor: 'success.main' },
  hybrid: { label: 'Both', tone: 'primary', themeColor: 'primary.main' },
};

// ============================================================================
// SUPPLY MODE
// ============================================================================

const SUPPLY_MODE_MAP = {
  supply_only: { label: 'Supply Only', tone: 'info', themeColor: 'info.main' },
  demand_only: { label: 'Demand Only', tone: 'warning', themeColor: 'warning.main' },
  both: { label: 'Both', tone: 'primary', themeColor: 'primary.main' },
};

// ============================================================================
// GENERIC RESOLVER
// ============================================================================

const MAPS = {
  projectStatus: PROJECT_STATUS_MAP,
  countFreshness: COUNT_FRESHNESS_MAP,
  entryChannel: ENTRY_CHANNEL_MAP,
  capability: CAPABILITY_MAP,
  supplyMode: SUPPLY_MODE_MAP,
};

const FALLBACK = { label: 'Unknown', tone: 'neutral', themeColor: 'grey.500' };

/**
 * Resolve a status value to its tone mapping.
 * 
 * @param {string} domain - One of: projectStatus, countFreshness, entryChannel, capability, supplyMode
 * @param {string} value - The status value to resolve
 * @returns {{ label: string, tone: string, themeColor: string }}
 */
export function resolveStatusTone(domain, value) {
  const map = MAPS[domain];
  if (!map) return FALLBACK;
  return map[value] || FALLBACK;
}

// Convenience exports for direct access
export {
  PROJECT_STATUS_MAP,
  COUNT_FRESHNESS_MAP,
  ENTRY_CHANNEL_MAP,
  CAPABILITY_MAP,
  SUPPLY_MODE_MAP,
};
